import { useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'

import { Toaster } from '@grupoboticario/flora-react'

import { Guard } from '@/components/Guard/Guard'
import { Layout } from '@/components/Layout/Layout'
import { UserProvider, useUserContext } from '@/context/user'
import { NotFoundPage } from '@/pages/404'
import { EditAcceleratorCampaignPage } from '@/pages/acceleration/EditAcceleratorCampaignPage'
import { AccelerationPage } from '@/pages/acceleration/index'
import { NewAccelerationPage } from '@/pages/acceleration/new'
import { BenefitEventPage } from '@/pages/benefits/event/index'
import { NewEventRedirect } from '@/pages/benefits/event/new/index'
import { BenefitEventsPage } from '@/pages/benefits/events/index'
import { HomePage } from '@/pages/home/index'
import { LoginPage } from '@/pages/index'
import { EditMemberPage } from '@/pages/members/edit'
import { MembersPage } from '@/pages/members/index'
import { NewMemberPage } from '@/pages/members/new'
import { OauthPage } from '@/pages/oauth'
import { isBrowser } from '@/utils/isBrowser'
import { getPageTitle } from '@/utils/pageTitleResolver'
import { isRouteValid } from '@/utils/routeUtils'

const SessionGuard = () => {
  const { isSessionValid, hasMinimumPermissionsToLogin, user, logout, checkForToken } = useUserContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = location
  const isUserHydrated = !isSessionValid() || Object.keys(user).length > 0

  useEffect(() => {
    checkForToken()
  }, [checkForToken])

  useEffect(() => {
    if (isBrowser() && !isSessionValid() && pathname !== '/' && !pathname.includes('/oauth')) {
      navigate('/')
    }
  }, [pathname, isSessionValid, navigate])

  useEffect(() => {
    if (isBrowser() && isSessionValid() && pathname === '/') {
      navigate('/home')
    }
  }, [pathname, isSessionValid, navigate])

  useEffect(() => {
    if (!isUserHydrated) return

    if (
      isBrowser() &&
      isSessionValid() &&
      pathname !== '/' &&
      !pathname.includes('/oauth') &&
      !hasMinimumPermissionsToLogin((user.roles as string[]) ?? [])
    ) {
      logout()
      Toaster.notify({
        kind: 'error',
        description:
          'Parece que você ainda não tem acesso ao Backoffice, mas pode requisitar acesso pelo Meu Acesso GB.'
      })
    }
  }, [pathname, isUserHydrated, isSessionValid, hasMinimumPermissionsToLogin, user, logout])

  return null
}

const AppRouter = () => {
  const location = useLocation()
  const { pathname, search, hash } = location
  const asPath = pathname + search + hash

  const isAuthPage = pathname === '/' || pathname.includes('/oauth')
  const pageTitle = !isAuthPage ? getPageTitle(pathname, asPath) : undefined

  return (
    <UserProvider>
      <SessionGuard />
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/oauth" element={<OauthPage />} />
        <Route
          path="/*"
          element={
            <Guard asPath={asPath}>
              <Layout title={pageTitle}>
                <Routes>
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/members" element={<MembersPage />} />
                  <Route path="/members/new" element={<NewMemberPage />} />
                  <Route path="/members/:member" element={<EditMemberPage />} />
                  <Route path="/acceleration" element={<AccelerationPage />} />
                  <Route path="/acceleration/new" element={<NewAccelerationPage />} />
                  <Route path="/acceleration/:campaignId/edit" element={<EditAcceleratorCampaignPage />} />
                  <Route path="/acceleration/:campaignId" element={<EditAcceleratorCampaignPage />} />
                  <Route path="/benefits/events" element={<BenefitEventsPage />} />
                  <Route path="/benefits/event/new" element={<NewEventRedirect />} />
                  <Route path="/benefits/event" element={<BenefitEventPage />} />
                  <Route path="/events/:eventId" element={<BenefitEventPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Layout>
            </Guard>
          }
        />
      </Routes>
    </UserProvider>
  )
}

export { AppRouter, isRouteValid }
